import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaBroom, FaChevronLeft, FaDownload, FaEllipsisV, FaPlus, FaRedo, FaSlidersH } from 'react-icons/fa';
import { showAdminError, showAdminSuccess } from '../Common/adminResponseAlert';
import Navbar from '../../../../ReusableBlocks/Navbar';
import * as PATH from '../../../../CentralisedLink';
import {
  buildFaultPayload,
  buildLabelsPayload,
  buildVersionFeaturesUpsertPayload,
  buildVersionFeaturesDeletePayload,
  buildDeleteTenantPayload,
  buildReleaseTenantPayload,
  buildResetPasswordPayload,
  buildTenantEditPayload,
  resolveTenantItsmUrl,
  TENANT_ITSM_URL_UNAVAILABLE_MESSAGE,
  canDeleteTenant,
  canEditTenant,
  canReleaseTenant,
  downloadTenantCsv,
  getMainTenantStatus,
  getPasswordResetResultSummary,
  getStatusConfirmationMessage,
  getStatusSuccessMessage,
  getTenantActions,
  getTenantDisplayName,
  getUniqueLabels,
  getUniqueTenantFeatures,
  tenantFeaturesPayloadHasChanges,
  matchesTenantSearch,
  UI_STATUS_OPTIONS,
} from './tenantHelpers';
import {
  fetchAdminSharedTenantsAct,
} from '../../../../Redux/Dashboard/External/AdminTenants/Actions/FetchAdminSharedTenantsAction';
import {
  clearAdminSharedTenantDetailAct,
  fetchAdminSharedTenantDetailAct,
} from '../../../../Redux/Dashboard/External/AdminTenants/Actions/FetchAdminSharedTenantDetailAction';
import {
  updateAdminBookingLabelsAct,
  updateAdminBookingTenantFeaturesAct,
  fetchAdminBookingVersionFeaturesAct,
  upsertAdminBookingVersionFeaturesAct,
  deleteAdminBookingVersionFeaturesAct,
  updateAdminFaultStatusAct,
  updateAdminSharedTenantStatusAct,
  updateAdminSharedTenantEditAct,
  onboardAdminSharedTenantAct,
  resetAdminSharedTenantPasswordsAct,
  runAdminSharedTenantCleanupAct,
  deleteAdminSharedTenantAct,
  releaseAdminSharedTenantAct,
  clearAdminSharedTenantMutationErrorsAct,
} from '../../../../Redux/Dashboard/External/AdminTenants/Actions/AdminSharedTenantMutationsActions';
import ChangeStatusModal from './Components/ChangeStatusModal';
import CleanupResultModal from './Components/CleanupResultModal';
import ClearFaultModal from './Components/ClearFaultModal';
import DeleteTenantModal from './Components/DeleteTenantModal';
import FaultTenantModal from './Components/FaultTenantModal';
import ManageFeaturesModal from './Components/ManageFeaturesModal';
import ManageLabelsModal from './Components/ManageLabelsModal';
import OnboardTenantWizard from './Components/OnboardTenantWizard';
import PasswordResetResultModal from './Components/PasswordResetResultModal';
import ResetTenantPasswordsModal from './Components/ResetTenantPasswordsModal';
import ReleaseTenantModal from './Components/ReleaseTenantModal';
import TenantDetailsDrawer from './Components/TenantDetailsDrawer';
import TenantTable from './Components/TenantTable';
import TenantTabs from './Components/TenantTabs';
import TenantToolbar from './Components/TenantToolbar';

const defaultFilters = { status: '', label: '' };

const TenantsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const headerMenuRef = useRef(null);

  const {
    loading: isListLoading,
    data: tenants,
    error: listError,
  } = useSelector((state) => state.AdminSharedTenantsList || { loading: false, data: [], error: null });

  const {
    loading: isDetailLoading,
    tenant: detailTenant,
    error: detailError,
    requestKey: detailRequestKey,
  } = useSelector((state) => state.AdminSharedTenantDetail || {
    loading: false,
    tenant: null,
    error: null,
    requestKey: null,
  });

  const {
    statusUpdate,
    labelsUpdate,
    tenantFeaturesUpdate,
    faultUpdate,
    editUpdate,
    onboardUpdate,
    passwordReset,
    cleanup,
    deleteTenant,
    releaseTenant,
    versionFeaturesAdmin,
  } = useSelector((state) => state.AdminSharedTenantMutations || {
    statusUpdate: { loading: false, error: null },
    labelsUpdate: { loading: false, error: null },
    tenantFeaturesUpdate: { loading: false, error: null },
    faultUpdate: { loading: false, error: null },
    editUpdate: { loading: false, error: null },
    onboardUpdate: { loading: false, error: null },
    passwordReset: { loading: false, error: null, lastResult: null },
    cleanup: { loading: false, error: null, lastResult: null },
    deleteTenant: { loading: false, error: null },
    releaseTenant: { loading: false, error: null },
    versionFeaturesAdmin: {
      list: [],
      loading: false,
      error: null,
      updating: false,
      deleting: false,
    },
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [filters, setFilters] = useState(defaultFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [drawerMode, setDrawerMode] = useState('view');
  const [modalState, setModalState] = useState(null);
  const [isOnboardOpen, setIsOnboardOpen] = useState(false);
  const [manageFeatures, setManageFeatures] = useState({
    isOpen: false,
    tab: 'tenant',
    preselectedTenantId: null,
  });
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [pendingDrawerOpen, setPendingDrawerOpen] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [passwordResetResult, setPasswordResetResult] = useState(null);
  const [cleanupErrors, setCleanupErrors] = useState(null);

  const openManageFeatures = ({ tab = 'tenant', preselectedTenantId = null } = {}) => {
    setManageFeatures({ isOpen: true, tab, preselectedTenantId });
  };

  const closeManageFeatures = () => {
    dispatch(clearAdminSharedTenantMutationErrorsAct());
    setManageFeatures({ isOpen: false, tab: 'tenant', preselectedTenantId: null });
  };

  useEffect(() => {
    dispatch(fetchAdminSharedTenantsAct());
  }, [dispatch]);

  useEffect(() => {
    if (!manageFeatures.isOpen) return;
    dispatch(fetchAdminBookingVersionFeaturesAct()).catch(() => {});
  }, [manageFeatures.isOpen, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(event.target)) {
        setIsHeaderMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId) || null,
    [selectedTenantId, tenants]
  );

  const drawerTenant = useMemo(() => {
    if (!selectedTenant) return null;
    if (detailTenant && detailTenant.id === selectedTenant.id) return detailTenant;
    return selectedTenant;
  }, [selectedTenant, detailTenant]);

  const modalTenant = useMemo(() => {
    if (!modalState?.tenantId) return null;
    const listTenant = tenants.find((tenant) => tenant.id === modalState.tenantId);
    if (!listTenant) return null;
    if (detailTenant && detailTenant.id === listTenant.id) return detailTenant;
    return listTenant;
  }, [modalState, tenants, detailTenant]);

  const labelOptions = useMemo(() => getUniqueLabels(tenants), [tenants]);
  const tenantFeatureOptions = useMemo(() => getUniqueTenantFeatures(tenants), [tenants]);
  const activeTenants = useMemo(() => tenants.filter((tenant) => tenant.isActive !== false), [tenants]);

  const tabCounts = useMemo(() => ({
    all: activeTenants.length,
    Internal: activeTenants.filter((t) => t.tenantType === 'Internal').length,
    External: activeTenants.filter((t) => t.tenantType === 'External').length,
    Technical: activeTenants.filter((t) => t.tenantType === 'Technical').length,
    'Pre-GA': activeTenants.filter((t) => t.tenantType === 'Pre-GA').length,
  }), [activeTenants]);

  const filteredTenants = useMemo(() => (
    activeTenants
      .filter((tenant) => activeTab === 'all' || tenant.tenantType === activeTab)
      .filter((tenant) => !filters.status || getMainTenantStatus(tenant) === filters.status)
      .filter((tenant) => !filters.label || (tenant.labels || []).includes(filters.label))
      .filter((tenant) => matchesTenantSearch(tenant, searchTerm))
  ), [activeTenants, activeTab, filters, searchTerm]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.status) chips.push({ key: 'status', label: filters.status });
    if (filters.label) chips.push({ key: 'label', label: filters.label });
    return chips;
  }, [filters]);

  const hasFilters = Boolean(filters.status || filters.label);
  const hasSearch = Boolean(searchTerm.trim());
  const isInitialLoading = isListLoading && tenants.length === 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filters, searchTerm]);

  const closeModal = () => {
    dispatch(clearAdminSharedTenantMutationErrorsAct());
    setModalState(null);
  };

  const loadTenantDetail = (tenant) => {
    if (!tenant) return;
    dispatch(fetchAdminSharedTenantDetailAct(tenant.tenantType, tenant.tenantNumber));
  };

  const openDrawer = (tenant, mode = 'view') => {
    if (selectedTenant && drawerMode === 'edit' && tenant.id !== selectedTenant.id) {
      setPendingDrawerOpen({ tenant, mode });
      return;
    }
    setPendingDrawerOpen(null);
    setSelectedTenantId(tenant.id);
    setDrawerMode(mode);
    loadTenantDetail(tenant);
  };

  const confirmDiscardNavigation = ({ tenant, mode }) => {
    setPendingDrawerOpen(null);
    setSelectedTenantId(tenant.id);
    setDrawerMode(mode);
    loadTenantDetail(tenant);
  };

  const closeDrawer = () => {
    setPendingDrawerOpen(null);
    setSelectedTenantId('');
    setDrawerMode('view');
    dispatch(clearAdminSharedTenantDetailAct());
  };

  const refreshTenants = async () => {
    setIsRefreshing(true);
    await dispatch(fetchAdminSharedTenantsAct());
    setIsRefreshing(false);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setSearchTerm('');
  };

  const removeFilterChip = (key) => {
    setFilters((prev) => ({ ...prev, [key]: '' }));
  };

  const refetchAfterMutation = async (tenant) => {
    await dispatch(fetchAdminSharedTenantsAct());
    if (tenant && selectedTenantId === tenant.id) {
      dispatch(fetchAdminSharedTenantDetailAct(tenant.tenantType, tenant.tenantNumber));
    }
  };

  const refetchAfterVersionFeaturesMutation = async () => {
    await dispatch(fetchAdminBookingVersionFeaturesAct());
    await dispatch(fetchAdminSharedTenantsAct());
    if (selectedTenant) {
      dispatch(fetchAdminSharedTenantDetailAct(selectedTenant.tenantType, selectedTenant.tenantNumber));
    }
  };

  const handleSaveVersionFeaturesDefinition = async (entry, isCreate) => {
    try {
      await dispatch(upsertAdminBookingVersionFeaturesAct(buildVersionFeaturesUpsertPayload([entry])));
      await refetchAfterVersionFeaturesMutation();
      showAdminSuccess(
        isCreate
          ? 'Version features added successfully.'
          : 'Version features updated successfully.'
      );
      return true;
    } catch (error) {
      showAdminError('Unable to save version features', error?.message || 'Please try again.');
      return false;
    }
  };

  const handleDeleteVersionFeaturesDefinition = async (label) => {
    try {
      await dispatch(deleteAdminBookingVersionFeaturesAct(buildVersionFeaturesDeletePayload([label])));
      await refetchAfterVersionFeaturesMutation();
      showAdminSuccess('Version features deleted successfully.');
      return true;
    } catch (error) {
      showAdminError('Unable to delete version features', error?.message || 'Please try again.');
      return false;
    }
  };

  const handleSaveEdit = async ({ draft, editingSecrets }) => {
    const tenant = drawerTenant;
    if (!tenant || !canEditTenant(tenant)) return;

    const editPayload = buildTenantEditPayload(tenant, draft, editingSecrets);
    if (!editPayload) return;

    try {
      await dispatch(updateAdminSharedTenantEditAct(editPayload));
      setDrawerMode('view');
      await refetchAfterMutation(tenant);
      showAdminSuccess(`${getTenantDisplayName(tenant)} updated successfully`);
    } catch (error) {
      showAdminError('Unable to update tenant', error?.message || 'Please try again.');
    }
  };

  const handleSaveLabels = async (labels) => {
    if (!modalTenant) return;

    const payload = buildLabelsPayload(modalTenant, modalTenant.labels || [], labels);
    const hasChanges = Object.values(payload).some(
      (group) => group.some((entry) => entry.add.length > 0 || entry.remove.length > 0)
    );
    if (!hasChanges) return;

    try {
      await dispatch(updateAdminBookingLabelsAct(payload));
      closeModal();
      await refetchAfterMutation(modalTenant);
      showAdminSuccess('Labels updated successfully.');
    } catch (error) {
      showAdminError('Unable to update labels', error?.message || 'Please try again.');
    }
  };

  const handleApplyTenantFeatures = async (payload) => {
    const hasEntries = tenantFeaturesPayloadHasChanges(payload);
    if (!hasEntries) return false;

    try {
      await dispatch(updateAdminBookingTenantFeaturesAct(payload));
      await dispatch(fetchAdminSharedTenantsAct());
      if (selectedTenant) {
        dispatch(fetchAdminSharedTenantDetailAct(selectedTenant.tenantType, selectedTenant.tenantNumber));
      }
      showAdminSuccess('Tenant features updated successfully.');
      return true;
    } catch (error) {
      showAdminError('Unable to update tenant features', error?.message || 'Please try again.');
      return false;
    }
  };

  const handleStatusConfirm = async (newStatus) => {
    if (!modalTenant) return;

    const { title, text } = getStatusConfirmationMessage(modalTenant, newStatus);
    const confirm = await Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    try {
      await dispatch(updateAdminSharedTenantStatusAct(modalTenant, newStatus));
      closeModal();
      await refetchAfterMutation(modalTenant);
      showAdminSuccess(getStatusSuccessMessage(modalTenant, newStatus));
    } catch (error) {
      showAdminError('Unable to change status', error?.message || 'Please try again.');
    }
  };

  const handleFaultConfirm = async () => {
    if (!modalTenant) return;

    try {
      await dispatch(updateAdminFaultStatusAct(buildFaultPayload(modalTenant, true)));
      closeModal();
      await refetchAfterMutation(modalTenant);
      showAdminSuccess(`${getTenantDisplayName(modalTenant)} marked as faulted.`);
    } catch (error) {
      if (error?.shouldRefetch) {
        await refetchAfterMutation(modalTenant);
        closeModal();
      }
      showAdminError('Unable to mark as faulted', error?.message || 'Please try again.');
    }
  };

  const handleUnfaultConfirm = async () => {
    if (!modalTenant) return;

    try {
      await dispatch(updateAdminFaultStatusAct(buildFaultPayload(modalTenant, false)));
      closeModal();
      await refetchAfterMutation(modalTenant);
      showAdminSuccess(`Fault cleared for ${getTenantDisplayName(modalTenant)}.`);
    } catch (error) {
      if (error?.shouldRefetch) {
        await refetchAfterMutation(modalTenant);
        closeModal();
      }
      showAdminError('Unable to clear fault', error?.message || 'Please try again.');
    }
  };

  const handleResetPasswordsConfirm = async (userList, authCredentials) => {
    if (!modalTenant) return;

    if (!resolveTenantItsmUrl(modalTenant)) {
      showAdminError('Unable to reset passwords', TENANT_ITSM_URL_UNAVAILABLE_MESSAGE);
      return;
    }

    const payload = buildResetPasswordPayload(modalTenant, userList, authCredentials);

    try {
      const result = await dispatch(resetAdminSharedTenantPasswordsAct(payload));
      closeModal();
      const summary = getPasswordResetResultSummary(result);

      if (summary.isFullSuccess) {
        showAdminSuccess(
          `Passwords reset successfully for ${summary.successfulCount} user${summary.successfulCount === 1 ? '' : 's'}.`
        );
        return;
      }

      if (summary.isTotalFailure) {
        showAdminError(
          'Password reset failed',
          summary.failedResets.length
            ? summary.failedResets.map((entry) => `${entry.user}: ${entry.reason || 'Reset failed.'}`).join('\n')
            : 'No users could be reset.'
        );
        return;
      }

      setPasswordResetResult(result);
    } catch (error) {
      showAdminError('Unable to reset passwords', error?.message || 'Please try again.');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!modalTenant || !canDeleteTenant(modalTenant)) return;

    const payload = buildDeleteTenantPayload(modalTenant);

    try {
      await dispatch(deleteAdminSharedTenantAct(payload));
      closeModal();
      if (selectedTenantId === modalTenant.id) {
        closeDrawer();
      }
      await dispatch(fetchAdminSharedTenantsAct());
      showAdminSuccess(`${getTenantDisplayName(modalTenant)} deleted successfully.`);
    } catch (error) {
      showAdminError('Unable to delete tenant', error?.message || 'Please try again.');
    }
  };

  const handleReleaseConfirm = async () => {
    if (!modalTenant || !canReleaseTenant(modalTenant) || releaseTenant.loading) return;

    const payload = buildReleaseTenantPayload(modalTenant);
    if (!payload.bookingEndTime || payload.tenantNumber == null || !payload.bookingTenantType) {
      showAdminError(
        'Unable to release tenant',
        'Booking information is missing. Tenant data will be refreshed.'
      );
      await refetchAfterMutation(modalTenant);
      closeModal();
      return;
    }

    try {
      await dispatch(releaseAdminSharedTenantAct(modalTenant));
      closeModal();
      await refetchAfterMutation(modalTenant);
      showAdminSuccess(`${getTenantDisplayName(modalTenant)} released successfully.`);
    } catch (error) {
      if (error?.shouldRefetch) {
        await refetchAfterMutation(modalTenant);
        closeModal();
      }
      showAdminError('Unable to release tenant', error?.message || 'Please try again.');
    }
  };

  const handleRunCleanup = async () => {
    setIsHeaderMenuOpen(false);

    const confirm = await Swal.fire({
      title: 'Run shared tenant cleanup?',
      html: '<p>This will immediately run cleanup for all shared tenants with expired bookings that are not faulted, instead of waiting for the scheduled cleanup cycle.</p><p>The process may take several minutes.</p><p class="stm-muted">Active and faulted bookings are skipped.</p>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Run Cleanup',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    try {
      const result = await dispatch(runAdminSharedTenantCleanupAct());
      await dispatch(fetchAdminSharedTenantsAct());
      if (selectedTenant) {
        dispatch(fetchAdminSharedTenantDetailAct(selectedTenant.tenantType, selectedTenant.tenantNumber));
      }

      if (result?.errors?.length) {
        setCleanupErrors(result.errors);
        return;
      }

      showAdminSuccess('Shared tenant cleanup completed.');
    } catch (error) {
      showAdminError('Unable to run cleanup', error?.message || 'Please try again.');
    }
  };

  const handleOnboardSave = async (form) => {
    try {
      const result = await dispatch(onboardAdminSharedTenantAct(form));
      setIsOnboardOpen(false);
      await dispatch(fetchAdminSharedTenantsAct());
      if (result?.result?.tenantType) {
        setActiveTab(result.result.tenantType);
      }
      showAdminSuccess('Tenant onboarded successfully.');
      return result;
    } catch (error) {
      showAdminError('Unable to onboard tenant', error?.message || 'Please try again.');
      throw error;
    }
  };

  const handleTenantAction = (actionKey, tenant) => {
    if (actionKey === 'view') openDrawer(tenant, 'view');
    if (actionKey === 'edit' && canEditTenant(tenant)) openDrawer(tenant, 'edit');
    if (actionKey === 'labels') setModalState({ type: 'labels', tenantId: tenant.id });
    if (actionKey === 'status') setModalState({ type: 'status', tenantId: tenant.id });
    if (actionKey === 'fault') setModalState({ type: 'fault', tenantId: tenant.id });
    if (actionKey === 'unfault') setModalState({ type: 'unfault', tenantId: tenant.id });
    if (actionKey === 'resetPasswords') setModalState({ type: 'resetPasswords', tenantId: tenant.id });
    if (actionKey === 'release' && canReleaseTenant(tenant)) setModalState({ type: 'release', tenantId: tenant.id });
    if (actionKey === 'delete' && canDeleteTenant(tenant)) setModalState({ type: 'delete', tenantId: tenant.id });
  };

  const handleRetryDetail = () => {
    if (selectedTenant) loadTenantDetail(selectedTenant);
  };

  const getEmptyMessage = () => {
    if (listError) return listError;
    if (activeTenants.length === 0 && !isInitialLoading) return 'No shared tenants have been onboarded yet.';
    if (hasSearch) return 'No tenants match your search.';
    if (hasFilters) return 'No tenants match the selected filters.';
    if (activeTab !== 'all') return `No ${activeTab} shared tenants found.`;
    return 'No tenants available.';
  };

  const emptyState = (
    <div className="stm-empty-state">
      <p>{getEmptyMessage()}</p>
      {listError && (
        <button type="button" className="stm-btn stm-btn--secondary" onClick={() => dispatch(fetchAdminSharedTenantsAct())}>
          Retry
        </button>
      )}
      {!listError && activeTenants.length === 0 && (
        <button type="button" className="stm-btn stm-btn--primary" onClick={() => setIsOnboardOpen(true)}>
          Onboard Tenant
        </button>
      )}
      {!listError && (hasFilters || hasSearch) && (
        <button type="button" className="stm-btn stm-btn--secondary" onClick={clearFilters}>Clear filters</button>
      )}
    </div>
  );

  const detailMatchesSelection = Boolean(
    selectedTenant && detailTenant && detailTenant.id === selectedTenant.id && detailRequestKey === selectedTenant.id
  );

  return (
    <>
      <Navbar activeTab="ADMIN" />
      <main className="stm-page">
        <div className="stm-workspace">
          <header className="stm-header">
            <div className="stm-header__left">
              <button type="button" className="stm-back-btn" onClick={() => navigate(PATH.ADMIN_PATH)} aria-label="Back to Admin">
                <FaChevronLeft />
              </button>
              <div>
                <h1>Shared Tenant Management</h1>
                <p>Manage shared booking tenants and operations.</p>
              </div>
            </div>
            <div className="stm-header__actions">
              <button
                type="button"
                className="stm-btn stm-btn--secondary"
                onClick={() => openManageFeatures()}
              >
                <FaSlidersH /> Manage Features
              </button>
              <button type="button" className="stm-btn stm-btn--primary" onClick={() => setIsOnboardOpen(true)}>
                <FaPlus /> Onboard Tenant
              </button>
              <div className="stm-header-menu" ref={headerMenuRef}>
                <button
                  type="button"
                  className="stm-icon-btn"
                  aria-label="More actions"
                  onClick={() => setIsHeaderMenuOpen((prev) => !prev)}
                >
                  <FaEllipsisV />
                </button>
                {isHeaderMenuOpen && (
                  <div className="stm-header-menu__list">
                    <button type="button" onClick={() => { setIsHeaderMenuOpen(false); refreshTenants(); }} disabled={isRefreshing}>
                      <FaRedo /> {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button type="button" onClick={() => { setIsHeaderMenuOpen(false); downloadTenantCsv(filteredTenants); }}>
                      <FaDownload /> Export Tenant List
                    </button>
                    <button
                      type="button"
                      onClick={handleRunCleanup}
                      disabled={cleanup.loading || isRefreshing}
                    >
                      <FaBroom /> {cleanup.loading ? 'Running cleanup...' : 'Run Cleanup'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          <section className="stm-panel">
            <TenantTabs
              activeTab={activeTab}
              counts={tabCounts}
              onChange={(tab) => {
                setActiveTab(tab);
                setFilters(defaultFilters);
                setSearchTerm('');
              }}
            />
            <TenantToolbar
              searchTerm={searchTerm}
              filters={filters}
              labelOptions={labelOptions}
              statusOptions={UI_STATUS_OPTIONS}
              activeChips={activeChips}
              onSearchChange={setSearchTerm}
              onFilterChange={(field, value) => setFilters((prev) => ({ ...prev, [field]: value }))}
              onRemoveChip={removeFilterChip}
              onClearFilters={clearFilters}
            />
            <TenantTable
              tenants={isInitialLoading || listError ? [] : filteredTenants}
              getActions={getTenantActions}
              onOpen={(tenant) => openDrawer(tenant, 'view')}
              onAction={handleTenantAction}
              emptyState={emptyState}
              isLoading={isInitialLoading}
              activeTab={activeTab}
              currentPage={currentPage}
              rowsPerPage={10}
              onPageChange={setCurrentPage}
            />
          </section>
        </div>
      </main>

      <TenantDetailsDrawer
        tenant={drawerTenant}
        isOpen={Boolean(selectedTenant)}
        mode={drawerMode}
        detailLoading={isDetailLoading}
        detailLoaded={detailMatchesSelection}
        detailError={detailError}
        onRetryDetail={handleRetryDetail}
        onClose={closeDrawer}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={() => setDrawerMode('view')}
        onStartEdit={() => setDrawerMode('edit')}
        onAction={handleTenantAction}
        getActions={getTenantActions}
        isSaving={editUpdate.loading}
        pendingOpen={pendingDrawerOpen}
        onConfirmDiscard={confirmDiscardNavigation}
        onManageTenantFeatures={(tenantId) => openManageFeatures({ tab: 'tenant', preselectedTenantId: tenantId })}
      />

      <ManageFeaturesModal
        isOpen={manageFeatures.isOpen}
        onClose={closeManageFeatures}
        initialTab={manageFeatures.tab}
        preselectedTenantId={manageFeatures.preselectedTenantId}
        tenants={activeTenants}
        labelOptions={labelOptions}
        tenantFeatureOptions={tenantFeatureOptions}
        versionDefinitions={versionFeaturesAdmin.list}
        versionLoading={versionFeaturesAdmin.loading}
        versionError={versionFeaturesAdmin.error}
        versionUpdating={versionFeaturesAdmin.updating}
        versionDeleting={versionFeaturesAdmin.deleting}
        onApplyTenantFeatures={handleApplyTenantFeatures}
        onSaveVersionDefinition={handleSaveVersionFeaturesDefinition}
        onDeleteVersionDefinition={handleDeleteVersionFeaturesDefinition}
        tenantFeaturesSaving={tenantFeaturesUpdate.loading}
        tenantFeaturesError={tenantFeaturesUpdate.error}
      />
      <ManageLabelsModal
        tenant={modalTenant}
        labelSuggestions={labelOptions}
        isOpen={modalState?.type === 'labels'}
        onClose={closeModal}
        onSave={handleSaveLabels}
        isSaving={labelsUpdate.loading}
        error={labelsUpdate.error}
      />
      <ChangeStatusModal
        tenant={modalTenant}
        isOpen={modalState?.type === 'status'}
        onClose={closeModal}
        onConfirm={handleStatusConfirm}
        isSaving={statusUpdate.loading}
        error={statusUpdate.error}
      />
      <FaultTenantModal
        tenant={modalTenant}
        isOpen={modalState?.type === 'fault'}
        onClose={closeModal}
        onConfirm={handleFaultConfirm}
        isSaving={faultUpdate.loading}
        error={faultUpdate.error}
      />
      <ClearFaultModal
        tenant={modalTenant}
        isOpen={modalState?.type === 'unfault'}
        onClose={closeModal}
        onConfirm={handleUnfaultConfirm}
        isSaving={faultUpdate.loading}
        error={faultUpdate.error}
      />
      <ResetTenantPasswordsModal
        tenant={modalTenant}
        isOpen={modalState?.type === 'resetPasswords'}
        onClose={closeModal}
        onConfirm={handleResetPasswordsConfirm}
        isSaving={passwordReset.loading}
      />
      <DeleteTenantModal
        tenant={modalTenant}
        isOpen={modalState?.type === 'delete'}
        onClose={closeModal}
        onConfirm={handleDeleteConfirm}
        isSaving={deleteTenant.loading}
        error={deleteTenant.error}
      />
      <ReleaseTenantModal
        tenant={modalTenant}
        isOpen={modalState?.type === 'release'}
        onClose={closeModal}
        onConfirm={handleReleaseConfirm}
        isSaving={releaseTenant.loading}
        error={releaseTenant.error}
      />
      <PasswordResetResultModal
        result={passwordResetResult}
        isOpen={Boolean(passwordResetResult)}
        onClose={() => setPasswordResetResult(null)}
      />
      <CleanupResultModal
        errors={cleanupErrors}
        isOpen={Boolean(cleanupErrors?.length)}
        onClose={() => setCleanupErrors(null)}
      />
      <OnboardTenantWizard
        isOpen={isOnboardOpen}
        onClose={() => {
          dispatch(clearAdminSharedTenantMutationErrorsAct());
          setIsOnboardOpen(false);
        }}
        onSave={handleOnboardSave}
        isSaving={onboardUpdate.loading}
        onboardError={onboardUpdate.error}
      />
    </>
  );
};

export default TenantsPage;
